import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import {
  IAgentRetrievalTestingPayload,
  IChangeAgentStatusPayload,
  ICreateAgentPayload,
  IDeleteAgentPayload,
  IGetAgentByIdPayload,
  IGetAgentsPayload,
  ILMTQueryAgentPayload,
  IPublishAgentPayload,
  IProcessFilesPayload,
  IQueryAgentPayload,
  ISSEQueryAgentPayload,
  IUpdateAgentPersonaPayload,
  IUpdateAgentAIConfigurationPayload,
  IUpdateAgentWidgetPayload,
  IUpdateAgentPreferencesPayload,
  IWidgetColorSuggestionPayload,
} from '../agent-chat-module/types/agent.service.type';
import type { SaveShareRequest } from '../agent-chat-module/types/agent-sharing.type';
import { agentService } from '../agent-chat-module/services/agent.service';
import { useConfig } from 'hooks/use-config';
import { processFileStream } from '../agent-chat-module/utils/process-file-stream';

const AGENTS_INFINITE_PAGE_SIZE = 20;

export const useGetAgents = (
  options: IGetAgentsPayload,
  queryOptions?: { enabled?: boolean; scope?: string }
) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['agents', queryOptions?.scope ?? 'default', options],
    queryFn: () => agentService.getAgents(options, blocksKey),
    enabled: !!blocksKey && (queryOptions?.enabled ?? true),
  });
};

/** Infinite scroll for agent pickers (e.g. subagent modal). */
export const useGetAgentsInfinite = (
  payload: Omit<IGetAgentsPayload, 'limit' | 'offset'> & { limit?: number },
  queryOptions?: { enabled?: boolean; scope?: string }
) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const limit = payload.limit ?? AGENTS_INFINITE_PAGE_SIZE;

  return useInfiniteQuery({
    queryKey: [
      'agents',
      'infinite',
      queryOptions?.scope ?? 'default',
      { ...payload, limit },
    ],
    queryFn: ({ pageParam = 0 }) =>
      agentService.getAgents(
        {
          ...payload,
          limit,
          offset: pageParam,
        },
        blocksKey
      ),
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.agents.length, 0);
      if (totalFetched < lastPage.total_count) {
        return totalFetched;
      }
      return undefined;
    },
    initialPageParam: 0,
    enabled: !!blocksKey && (queryOptions?.enabled ?? true),
  });
};

export const useGetAgentById = (
  payload: IGetAgentByIdPayload,
  queryOptions?: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnMount?: boolean | 'always';
  }
) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['agent', payload],
    queryFn: () => agentService.getAgentById(payload, blocksKey),
    enabled: !!blocksKey && !!payload.id && (queryOptions?.enabled ?? true),
    staleTime: queryOptions?.staleTime ?? 0,
    refetchOnMount: queryOptions?.refetchOnMount ?? 'always',
  });
};

export const useGetGuardrailsCatalog = (queryOptions?: {
  enabled?: boolean;
  staleTime?: number;
}) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['agents', 'guardrails-catalog'],
    queryFn: () => agentService.getGuardrailsCatalog(blocksKey),
    enabled: !!blocksKey && (queryOptions?.enabled ?? true),
    staleTime: queryOptions?.staleTime ?? 1000 * 60 * 5, // 5 minutes cache
  });
};

export const useProcessFiles = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation({
    mutationKey: ['agent', 'process-files'],
    mutationFn: async (payload: IProcessFilesPayload) => {
      const reader = await agentService.processFiles(payload, blocksKey);
      return processFileStream(reader);
    },
  });
};

export const useCreateAgent = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['agent', 'create'],
    mutationFn: async (payload: ICreateAgentPayload) => {
      const response = await agentService.createAgent(payload, blocksKey);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
};

export const useUpdateAgentPreferences = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['agent', 'preferences'],
    mutationFn: async (payload: IUpdateAgentPreferencesPayload) => {
      await agentService.updateAgentPreferences(payload, blocksKey);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
};

export const useDeleteAgent = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['agent', 'delete'],
    mutationFn: async (payload: IDeleteAgentPayload) => {
      const response = await agentService.deleteAgent(payload, blocksKey);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
};

export const useUpdateAgentAIConfigurations = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['agents', 'update-ai-configurations'],
    mutationFn: async (payload: IUpdateAgentAIConfigurationPayload) => {
      const response = await agentService.updateAgentAIConfiguration(payload, blocksKey);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent'] });
    },
  });
};

export const useUpdateAgentPersona = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['agents', 'update-persona'],
    mutationFn: async (payload: IUpdateAgentPersonaPayload) => {
      const response = await agentService.updateAgentPersona(payload, blocksKey);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent'] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
};

export const useUpdateWidget = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['agent', 'widget'],
    mutationFn: async (payload: IUpdateAgentWidgetPayload) => {
      const response = await agentService.updateAgentWidget(payload, blocksKey);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent'] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
};

export const useQueryAgent = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation({
    mutationKey: ['agent', 'query'],
    mutationFn: async (payload: IQueryAgentPayload) => {
      const response = await agentService.query(payload, blocksKey);
      return response;
    },
  });
};

export const useLMTQueryAgent = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation({
    mutationKey: ['agent', 'lmt-query'],
    mutationFn: async (payload: ILMTQueryAgentPayload) => {
      const response = await agentService.lmtQuery(payload, blocksKey);
      return response;
    },
  });
};

export const useLMTQueryAgentSSE = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return {
    streamQuery: (payload: ILMTQueryAgentPayload) => agentService.lmtQueryStream(payload, blocksKey),
  };
};

export const useQuerySSE = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return {
    streamQuery: (payload: ISSEQueryAgentPayload) => agentService.queryStream(payload, blocksKey),
  };
};

export const useChangeAgentStatus = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['agent', 'change-status'],
    mutationFn: async (payload: IChangeAgentStatusPayload) => {
      const response = await agentService.changeAgentStatus(payload, blocksKey);
      return response;
    },
    onSuccess: (response) => {
      if (response.is_success) {
        queryClient.invalidateQueries({ queryKey: ['agents'] });
      }
    },
  });
};

export const useWidgetColorSuggestion = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation({
    mutationKey: ['widget', 'color-suggestion'],
    mutationFn: async (payload: IWidgetColorSuggestionPayload) => {
      const response = await agentService.widgetColorSuggestion(payload, blocksKey);
      return response;
    },
  });
};

export const useAgentRetrievalTesting = (agentId: string) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation({
    mutationKey: ['agent', 'retrieval-testing', agentId],
    mutationFn: async (payload: IAgentRetrievalTestingPayload) => {
      const response = await agentService.retrievalTesting(agentId, payload, blocksKey);
      return response;
    },
  });
};

export const usePublishAgent = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['agent', 'publish'],
    mutationFn: async (payload: IPublishAgentPayload) => {
      const response = await agentService.publishAgents(payload, blocksKey);
      return response;
    },
    onSuccess: (response) => {
      if (response.is_success) {
        queryClient.invalidateQueries({ queryKey: ['agents'] });
        queryClient.invalidateQueries({ queryKey: ['agent'] });
      }
    },
  });
};

export const useGetAgentShare = (agentId: string, enabled = true) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['agent', 'share', agentId],
    queryFn: () => agentService.getShare(agentId, blocksKey),
    enabled: !!blocksKey && !!agentId && enabled,
  });
};

export const useSaveAgentShare = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['agent', 'share', 'save'],
    mutationFn: async ({ agentId, payload }: { agentId: string; payload: SaveShareRequest }) => {
      return agentService.saveShare(agentId, payload, blocksKey);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['agent', 'share', variables.agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent'] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
};
